import { DayType, MonthSubMode } from '@griply/types';
import {
  getMonthDayOrdinals,
  getMonthDayWarning,
  getNextSelectedMonthDays,
  getProviderRecurrenceLimits,
  getWeekDays,
  MONTH_DAYS,
  parseMonthDaysFromRrule,
  parseMonthWeekDayFromRrule,
  ProviderRecurrenceLimits,
  setMonthDaysInRrule,
  setMonthSpecificDayInRrule,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'shared/components/ui/form-select';
import { RadioInput } from 'shared/components/ui/radio-input';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './form-body.style';
import { MonthDayGrid } from './month-day-grid';

export type MonthsFormProps = {
  rrule: string;
  onChange: (rrule: string) => void;
  weekStartsOn: WeekDays;
  // absent = no target provider (a habit or task schedule) — every option stays offered
  limits?: ProviderRecurrenceLimits;
};

export const RruleMonthsBody: React.FC<MonthsFormProps> = ({
  rrule,
  weekStartsOn,
  onChange,
  limits = getProviderRecurrenceLimits(),
}) => {
  const { t } = useTranslation();
  const selectedMonthDays = useMemo(
    () => parseMonthDaysFromRrule(rrule),
    [rrule],
  );
  const selectedWeekDay = useMemo(
    () => parseMonthWeekDayFromRrule(rrule),
    [rrule],
  );
  const initialOrdinalWeekDay = useMemo(
    () => getWeekDays(weekStartsOn)[0],
    [weekStartsOn],
  );

  const onChangeOption = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.value === MonthSubMode.SpecificDay) {
      return onChange(
        setMonthSpecificDayInRrule(rrule, {
          weekDay: initialOrdinalWeekDay,
          ordinal: limits.monthOrdinals[0],
        }),
      );
    }

    onChange(setMonthDaysInRrule(rrule, []));
  };

  const handleToggleMonthDay = (day: number) => {
    onChange(
      setMonthDaysInRrule(
        rrule,
        getNextSelectedMonthDays(selectedMonthDays, day, limits),
      ),
    );
  };

  const onChangeOrdinalDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setMonthSpecificDayInRrule(rrule, {
        weekDay: e.target.value as DayType | WeekDays,
        ordinal: selectedWeekDay.ordinal,
      }),
    );
  };

  const onChangeOrdinalNumber = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setMonthSpecificDayInRrule(rrule, {
        weekDay: selectedWeekDay.weekDay,
        ordinal: parseInt(e.target.value),
      }),
    );
  };

  const ordinalOptions = useMemo(
    () =>
      limits.monthOrdinals.map((ordinal) => ({
        label: t(
          `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label`,
        ),
        value: ordinal,
      })),
    [limits.monthOrdinals, t],
  );

  const ordinalDayOptions = useMemo(
    () =>
      getMonthDayOrdinals(weekStartsOn).map((weekDay) => ({
        label: t(
          `general.labels.habit.repeat.specific-days.days.${weekDay}.label`,
        ),
        value: weekDay,
      })),
    [t, weekStartsOn],
  );

  const monthDayWarning = useMemo(
    () => getMonthDayWarning(selectedMonthDays, t),
    [selectedMonthDays, t],
  );

  return (
    <Styled.Container>
      {/* Specific day */}
      <Styled.RadioContainer>
        <RadioInput
          id={MonthSubMode.SpecificDays}
          name="month-options"
          checked={!selectedWeekDay}
          onChange={onChangeOption}
          value={MonthSubMode.SpecificDays}
        >
          {t('forms.habit.fields.repeat.months.specific-days.label')}
        </RadioInput>

        <Styled.RadioBody $disabled={!!selectedWeekDay}>
          <MonthDayGrid
            days={MONTH_DAYS}
            selectedDays={selectedMonthDays}
            onToggleDay={handleToggleMonthDay}
            disabled={!!selectedWeekDay}
          />
          {!!monthDayWarning && (
            <Styled.MonthDayWarning>{monthDayWarning}</Styled.MonthDayWarning>
          )}
        </Styled.RadioBody>
      </Styled.RadioContainer>

      {/* based-on-month day */}
      <Styled.RadioContainer>
        <RadioInput
          id={MonthSubMode.SpecificDay}
          name="month-options"
          checked={!!selectedWeekDay}
          onChange={onChangeOption}
          value={MonthSubMode.SpecificDay}
        >
          {t('forms.habit.fields.repeat.months.specific-day.label')}
        </RadioInput>

        <Styled.RadioBody $disabled={false}>
          <Styled.TimesPerContainer>
            <FormSelect
              value={selectedWeekDay?.ordinal}
              options={ordinalOptions}
              onChange={onChangeOrdinalNumber}
              disabled={!selectedWeekDay}
              isSmall
            />

            <FormSelect
              value={selectedWeekDay?.weekDay}
              options={ordinalDayOptions}
              onChange={onChangeOrdinalDay}
              disabled={!selectedWeekDay}
              isSmall
            />
          </Styled.TimesPerContainer>
        </Styled.RadioBody>
      </Styled.RadioContainer>
    </Styled.Container>
  );
};
